import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Collapse,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import {
  format,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
} from 'date-fns';
import { lt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

/** Match WorkSchedule.tsx getStatusColor('day_off') */
const DAY_OFF_BG = 'rgba(70, 150, 70, 0.85)';

const MIN_CONSECUTIVE_VACATION_DAYS = 14;

type ComplianceStatus = 'ok' | 'confirmed' | 'gap';

interface DepartmentScheduleRow {
  id: string;
  date: string;
  status: string;
  startTime?: string;
  endTime?: string;
  isApproved: boolean;
}

interface DepartmentEmployeePayload {
  userId: string;
  name: string;
  department: string;
  schedules: DepartmentScheduleRow[];
}

interface VacationConfirmation {
  confirmed: boolean;
  note?: string;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
}

interface EmployeeVacationRow {
  userId: string;
  name: string;
  department: string;
  vacationDateSet: Set<string>;
  totalDays: number;
  longestStreak: number;
  meetsRule: boolean;
  adminConfirmed: boolean;
  confirmationNote: string;
  complianceStatus: ComplianceStatus;
}

function formatDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function collectStateHolidayDates(
  employees: DepartmentEmployeePayload[],
  yStart: string,
  yEnd: string
): Set<string> {
  const set = new Set<string>();
  for (const emp of employees) {
    for (const s of emp.schedules || []) {
      if (s.status !== 'state_holiday' || !s.date) continue;
      const d = s.date.slice(0, 10);
      if (d >= yStart && d <= yEnd) {
        set.add(d);
      }
    }
  }
  return set;
}

/** Longest run of approved vacation days where gaps may contain only state holidays (calendar bridge). */
function longestConsecutiveVacationStreakAllowingStateHolidays(
  sortedUniqueYmd: string[],
  holidayYmdSet: Set<string>
): number {
  if (sortedUniqueYmd.length === 0) return 0;
  let maxRun = 1;
  let run = 1;
  for (let i = 1; i < sortedUniqueYmd.length; i++) {
    const a = sortedUniqueYmd[i - 1].split('-').map(Number);
    const b = sortedUniqueYmd[i].split('-').map(Number);
    const prev = new Date(a[0], a[1] - 1, a[2]);
    const curr = new Date(b[0], b[1] - 1, b[2]);
    const diffDays = Math.round((curr.getTime() - prev.getTime()) / 86400000);
    if (diffDays === 1) {
      run += 1;
      maxRun = Math.max(maxRun, run);
    } else if (diffDays > 1) {
      let allIntermediateAreHolidays = true;
      const walk = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1);
      while (walk < curr) {
        const ymd = formatDateLocal(walk);
        if (!holidayYmdSet.has(ymd)) {
          allIntermediateAreHolidays = false;
          break;
        }
        walk.setDate(walk.getDate() + 1);
      }
      if (allIntermediateAreHolidays) {
        run += 1;
        maxRun = Math.max(maxRun, run);
      } else {
        run = 1;
      }
    } else {
      run = 1;
    }
  }
  return maxRun;
}

function resolveComplianceStatus(meetsRule: boolean, adminConfirmed: boolean): ComplianceStatus {
  if (meetsRule) return 'ok';
  if (adminConfirmed) return 'confirmed';
  return 'gap';
}

function buildEmployeeRows(
  employees: DepartmentEmployeePayload[],
  year: number,
  holidayYmdSet: Set<string>,
  confirmations: Record<string, VacationConfirmation>
): EmployeeVacationRow[] {
  const yStart = `${year}-01-01`;
  const yEnd = `${year}-12-31`;
  return employees.map((emp) => {
    const vacationDateSet = new Set(
      (emp.schedules || [])
        .filter(
          (s) =>
            s.status === 'day_off' &&
            s.isApproved !== false &&
            s.date &&
            s.date.slice(0, 10) >= yStart &&
            s.date.slice(0, 10) <= yEnd
        )
        .map((s) => s.date.slice(0, 10))
    );
    const sorted = Array.from(vacationDateSet).sort();
    const longestStreak = longestConsecutiveVacationStreakAllowingStateHolidays(sorted, holidayYmdSet);
    const meetsRule = longestStreak >= MIN_CONSECUTIVE_VACATION_DAYS;
    const confirmation = confirmations[emp.userId];
    const adminConfirmed = !meetsRule && !!confirmation?.confirmed;
    return {
      userId: emp.userId,
      name: emp.name,
      department: emp.department || '',
      vacationDateSet,
      totalDays: vacationDateSet.size,
      longestStreak,
      meetsRule,
      adminConfirmed,
      confirmationNote: confirmation?.note || '',
      complianceStatus: resolveComplianceStatus(meetsRule, adminConfirmed),
    };
  });
}

interface YearMonthGridProps {
  year: number;
  vacationDates: Set<string>;
  dateLocale: typeof lt | undefined;
}

const YearMonthGrid: React.FC<YearMonthGridProps> = ({ year, vacationDates, dateLocale }) => {
  const weekdayLabels = useMemo(() => {
    const monday = startOfWeek(new Date(2024, 5, 3), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: monday, end: new Date(monday.getTime() + 6 * 86400000) }).map((d) =>
      format(d, 'EEE', { locale: dateLocale })
    );
  }, [dateLocale]);

  const renderMonth = (monthIndex: number) => {
    const monthAnchor = new Date(year, monthIndex, 1);
    const monthStart = startOfMonth(monthAnchor);
    const monthEnd = endOfMonth(monthAnchor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    return (
      <Paper key={monthIndex} sx={{ p: 1.5 }} elevation={1}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {format(monthAnchor, 'LLLL yyyy', { locale: dateLocale })}
        </Typography>
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          {weekdayLabels.map((wd, wi) => (
            <Box key={wi} sx={{ flex: '1 1 14.28%', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}>
                {wd}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {days.map((day) => {
            const key = formatDateLocal(day);
            const inMonth = isSameMonth(day, monthAnchor);
            const isVacation = vacationDates.has(key);
            return (
              <Box
                key={`${monthIndex}-${key}`}
                sx={{
                  flex: '1 1 14.28%',
                  boxSizing: 'border-box',
                  p: 0.25,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: isVacation ? 600 : 400,
                    color: isVacation ? '#fff' : inMonth ? 'text.primary' : 'text.disabled',
                    bgcolor: isVacation ? DAY_OFF_BG : inMonth ? 'action.hover' : 'action.selected',
                    opacity: inMonth ? 1 : 0.45,
                  }}
                >
                  {format(day, 'd')}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        py: 1,
      }}
    >
      {Array.from({ length: 12 }, (_, m) => (
        <Box key={m}>{renderMonth(m)}</Box>
      ))}
    </Box>
  );
};

const VacationSchedule: React.FC = () => {
  const { token, hasPermission } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('lt') ? lt : undefined;
  const canManage = hasPermission('schedule.manage');

  const [year, setYear] = useState(() => new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rows, setRows] = useState<EmployeeVacationRow[]>([]);
  const [deptFilter, setDeptFilter] = useState('');
  const [hideCompliant, setHideCompliant] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogUser, setConfirmDialogUser] = useState<EmployeeVacationRow | null>(null);
  const [confirmNote, setConfirmNote] = useState('');
  const [confirmSaving, setConfirmSaving] = useState(false);

  const toggleExpand = (userId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const loadYear = useCallback(async () => {
    if (!token || !canManage) {
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;
    try {
      const [scheduleRes, confirmRes] = await Promise.all([
        fetch(
          `${getApiUrl(API_ENDPOINTS.WORK_SCHEDULE_DEPARTMENT_VIEW)}?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        ),
        fetch(`${getApiUrl(API_ENDPOINTS.USERS_VACATION_14DAY_CONFIRMATIONS)}?year=${year}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (!scheduleRes.ok) {
        throw new Error('failed');
      }

      const data = await scheduleRes.json();
      const list: DepartmentEmployeePayload[] = data.data || [];
      let loadedConfirmations: Record<string, VacationConfirmation> = {};
      if (confirmRes.ok) {
        const confirmData = await confirmRes.json();
        loadedConfirmations = confirmData.confirmations || {};
      }
      const holidayYmdSet = collectStateHolidayDates(list, startDate, endDate);
      setRows(buildEmployeeRows(list, year, holidayYmdSet, loadedConfirmations));
    } catch {
      setError(t('vacationSchedule.loadError'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token, year, t, canManage]);

  useEffect(() => {
    void loadYear();
  }, [loadYear]);

  const filteredRows = useMemo(() => {
    const q = deptFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (hideCompliant && r.complianceStatus !== 'gap') return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.department && r.department.toLowerCase().includes(q))
      );
    });
  }, [rows, deptFilter, hideCompliant]);

  const openConfirmDialog = (emp: EmployeeVacationRow) => {
    setConfirmDialogUser(emp);
    setConfirmNote(emp.confirmationNote || '');
    setConfirmDialogOpen(true);
  };

  const saveConfirmation = async (userId: string, confirmed: boolean, note = '') => {
    if (!token) return;
    setConfirmSaving(true);
    setError('');
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_VACATION_14DAY_CONFIRMATION(userId)), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ year, confirmed, note }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save');
      }

      setRows((prev) =>
        prev.map((row) => {
          if (row.userId !== userId) return row;
          const adminConfirmed = !row.meetsRule && confirmed;
          return {
            ...row,
            adminConfirmed,
            confirmationNote: confirmed ? note : '',
            complianceStatus: resolveComplianceStatus(row.meetsRule, adminConfirmed),
          };
        })
      );

      setConfirmDialogOpen(false);
      setConfirmDialogUser(null);
      setSuccess(
        confirmed
          ? t('vacationSchedule.confirmSuccess', '14 d. taisyklė patvirtinta kaip išimtis')
          : t('vacationSchedule.revokeSuccess', 'Patvirtinimas atšauktas')
      );
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vacationSchedule.confirmError', 'Nepavyko išsaugoti'));
    } finally {
      setConfirmSaving(false);
    }
  };

  const renderComplianceCell = (emp: EmployeeVacationRow) => {
    if (emp.complianceStatus === 'ok') {
      return <Chip size="small" color="success" label={t('vacationSchedule.complianceOk')} />;
    }
    if (emp.complianceStatus === 'confirmed') {
      return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip
            title={
              emp.confirmationNote
                ? t('vacationSchedule.complianceConfirmedTooltipNote', 'Admin patvirtinimas: {{note}}', {
                    note: emp.confirmationNote,
                  })
                : t(
                    'vacationSchedule.complianceConfirmedTooltip',
                    'Admin patvirtino 14 d. taisyklę kaip priimtiną išimtį šiems metams'
                  )
            }
          >
            <Chip size="small" color="info" label={t('vacationSchedule.complianceConfirmed', 'Patvirtinta')} />
          </Tooltip>
          <Button
            size="small"
            color="inherit"
            onClick={() => void saveConfirmation(emp.userId, false)}
            disabled={confirmSaving}
          >
            {t('vacationSchedule.revokeConfirm', 'Atšaukti')}
          </Button>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Tooltip title={t('vacationSchedule.warningStreak', { minDays: MIN_CONSECUTIVE_VACATION_DAYS })}>
          <Chip size="small" color="warning" label={t('vacationSchedule.complianceWarn')} />
        </Tooltip>
        <Button size="small" variant="outlined" onClick={() => openConfirmDialog(emp)} disabled={confirmSaving}>
          {t('vacationSchedule.confirmButton', 'Patvirtinti')}
        </Button>
      </Stack>
    );
  };

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('vacationSchedule.noPermission')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t('vacationSchedule.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('vacationSchedule.subtitleAdmin')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => setYear((y) => y - 1)} aria-label={t('vacationSchedule.previousYear')}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" component="span">
          {year}
        </Typography>
        <IconButton onClick={() => setYear((y) => y + 1)} aria-label={t('vacationSchedule.nextYear')}>
          <ChevronRight />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label={t('vacationSchedule.filterDepartment')}
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={hideCompliant}
              onChange={(e) => setHideCompliant(e.target.checked)}
              size="small"
            />
          }
          label={t('vacationSchedule.hideCompliant', 'Slėpti Atitinka')}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {!loading && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('vacationSchedule.ruleExplanationAdmin', { minDays: MIN_CONSECUTIVE_VACATION_DAYS })}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>{t('vacationSchedule.columnEmployee')}</TableCell>
                <TableCell>{t('vacationSchedule.columnDepartment')}</TableCell>
                <TableCell align="right">{t('vacationSchedule.columnVacationDays')}</TableCell>
                <TableCell align="right">{t('vacationSchedule.columnLongestStreak')}</TableCell>
                <TableCell>{t('vacationSchedule.columnCompliance')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {rows.length > 0
                      ? t('vacationSchedule.noFilterMatches')
                      : t('vacationSchedule.noEmployees')}
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((emp) => (
                <React.Fragment key={emp.userId}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(emp.userId)} aria-label="expand">
                        {expanded.has(emp.userId) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{emp.name}</TableCell>
                    <TableCell>{emp.department || '—'}</TableCell>
                    <TableCell align="right">{emp.totalDays}</TableCell>
                    <TableCell align="right">{emp.longestStreak}</TableCell>
                    <TableCell>{renderComplianceCell(emp)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded.has(emp.userId) ? undefined : 0 }}>
                      <Collapse in={expanded.has(emp.userId)} timeout="auto" unmountOnExit>
                        <YearMonthGrid year={year} vacationDates={emp.vacationDateSet} dateLocale={dateLocale} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={() => !confirmSaving && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('vacationSchedule.confirmDialogTitle', 'Patvirtinti 14 d. taisyklę')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'vacationSchedule.confirmDialogHelp',
              'Pažymėkite, kad šiam darbuotojui {{year}} m. 14 dienų reikalavimas laikomas įvykdytu priimtinomis sąlygomis (pvz. trumpas darbo stažas).',
              { year }
            )}
          </Typography>
          {confirmDialogUser && (
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {confirmDialogUser.name}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('vacationSchedule.confirmNoteLabel', 'Pastaba (nebūtina)')}
            value={confirmNote}
            onChange={(e) => setConfirmNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={confirmSaving}>
            {t('common.cancel', 'Atšaukti')}
          </Button>
          <Button
            variant="contained"
            disabled={confirmSaving || !confirmDialogUser}
            onClick={() => {
              if (confirmDialogUser) {
                void saveConfirmation(confirmDialogUser.userId, true, confirmNote.trim());
              }
            }}
          >
            {t('vacationSchedule.confirmButton', 'Patvirtinti')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VacationSchedule;
